import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePlanner } from '../context/PlannerContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiArrowRight, FiUser, FiPlus, FiX } = FiIcons;

function CorePhonicsSurvey() {
  const navigate = useNavigate();
  const { state, dispatch } = usePlanner();
  const [students, setStudents] = useState(state.students.length > 0 ? state.students : [createEmptyStudent()]);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);

  function createEmptyStudent() {
    return {
      name: '',
      // Alphabet Skills and Letter Sounds
      letterNamesUppercase: '', // A. /26
      letterNamesLowercase: '', // B. /26
      consonantSounds: '', // C. /21
      longVowelSounds: '', // D. /5
      shortVowelSounds: '', // /5
      // Reading and Decoding Skills
      shortVowelsCVC: '', // E. /15
      consonantBlendsShortVowels: '', // F. /15
      shortVowelsDigraphsTch: '', // G. /15
      rControlledVowels: '', // H. /15
      longVowelSpellings: '', // I. /15
      variantVowels: '', // J. /15
      lowFrequencySpellings: '', // K. /15
      multisyllabicWords: '' // L. /24
    };
  }

  const handleInputChange = (field, value) => {
    const updatedStudents = [...students];
    updatedStudents[currentStudentIndex] = {
      ...updatedStudents[currentStudentIndex],
      [field]: value
    };
    setStudents(updatedStudents);
  };

  const addStudent = () => {
    setStudents([...students, createEmptyStudent()]);
    setCurrentStudentIndex(students.length);
  };

  const deleteStudent = (indexToDelete) => {
    if (students.length === 1) return; // Don't allow deleting the last student
    
    const updatedStudents = students.filter((_, index) => index !== indexToDelete);
    setStudents(updatedStudents);
    
    // Adjust current student index if necessary
    if (indexToDelete === currentStudentIndex) {
      // If we're deleting the current student, switch to the previous one or first one
      setCurrentStudentIndex(indexToDelete > 0 ? indexToDelete - 1 : 0);
    } else if (indexToDelete < currentStudentIndex) {
      // If we're deleting a student before the current one, adjust the index
      setCurrentStudentIndex(currentStudentIndex - 1);
    }
  };

  const selectStudent = (index) => {
    setCurrentStudentIndex(index);
  };

  const processFormData = () => {
    // Convert raw scores to student data format for all students
    const processedStudents = students.map(formData => {
      // Calculate percentages from raw scores
      return {
        name: formData.name,
        letterNames: Math.round(((parseInt(formData.letterNamesUppercase) || 0) + (parseInt(formData.letterNamesLowercase) || 0)) / 52 * 100),
        letterSounds: Math.round(((parseInt(formData.consonantSounds) || 0) + (parseInt(formData.longVowelSounds) || 0) + (parseInt(formData.shortVowelSounds) || 0)) / 31 * 100),
        vcCvc: Math.round((parseInt(formData.shortVowelsCVC) || 0) / 15 * 100),
        cvccCcvc: Math.round((parseInt(formData.consonantBlendsShortVowels) || 0) / 15 * 100),
        cvce: Math.round((parseInt(formData.longVowelSpellings) || 0) / 15 * 100),
        rControlled: Math.round((parseInt(formData.rControlledVowels) || 0) / 15 * 100),
        consonantDigraphs: Math.round((parseInt(formData.shortVowelsDigraphsTch) || 0) / 15 * 100),
        vowelDigraphs: Math.round((parseInt(formData.variantVowels) || 0) / 15 * 100),
        prefixesSuffixes: Math.round((parseInt(formData.lowFrequencySpellings) || 0) / 15 * 100),
        twoSyllable: Math.round((parseInt(formData.multisyllabicWords) || 0) / 24 * 100),
        threeSyllable: Math.round((parseInt(formData.multisyllabicWords) || 0) / 24 * 100),
        fourSyllable: Math.round((parseInt(formData.multisyllabicWords) || 0) / 24 * 100)
      };
    }).filter(student => student.name.trim() !== ''); // Only include students with names

    dispatch({ type: 'SET_ASSESSMENT_TYPE', payload: 'CORE' });
    dispatch({ type: 'SET_STUDENTS', payload: processedStudents });
    navigate('/groups');
  };

  const isCurrentStudentValid = () => {
    const currentStudent = students[currentStudentIndex];
    return currentStudent.name.trim() && 
           Object.values(currentStudent).some(value => 
             value !== '' && value !== currentStudent.name
           );
  };

  const currentStudent = students[currentStudentIndex];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white shadow-sm border-b"
      >
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/assessment')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              Back to Assessment Input
            </button>
            <h1 className="text-xl font-semibold text-thrive-slate font-display">CORE Phonics Survey</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </motion.header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
        >
          {/* Form Header - changed from black to #445a68 */}
          <div className="bg-thrive-slate text-white px-6 py-4">
            <h2 className="text-xl font-bold text-center tracking-wider">CORE Phonics Survey—Record Form</h2>
          </div>

          <div className="p-6">
            {/* Student Tabs with Delete Buttons */}
            {students.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-gray-200">
                {students.map((student, index) => (
                  <div
                    key={index}
                    className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      currentStudentIndex === index
                        ? 'bg-thrive-teal text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <button
                      onClick={() => selectStudent(index)}
                      className="pr-6"
                    >
                      {student.name || `Student ${index + 1}`}
                    </button>
                    {students.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteStudent(index);
                        }}
                        className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                        title="Delete student"
                      >
                        <SafeIcon icon={FiX} className="text-xs" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Student Info Section - removed grade and date fields */}
            <div className="mb-8 pb-6 border-b border-gray-200">
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <SafeIcon icon={FiUser} className="inline mr-1" />
                  Student Name
                </label>
                <input
                  type="text"
                  value={currentStudent.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                  placeholder="Enter student name"
                />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-thrive-slate mb-6 tracking-wider">SKILLS SUMMARY</h3>

            {/* Alphabet Skills and Letter Sounds Section */}
            <div className="mb-8">
              <h4 className="text-base font-semibold text-thrive-slate mb-4">Alphabet Skills and Letter Sounds</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="26"
                      value={currentStudent.letterNamesUppercase}
                      onChange={(e) => handleInputChange('letterNamesUppercase', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/26</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">A.</span>
                    <span>Letter names—uppercase</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="26"
                      value={currentStudent.letterNamesLowercase}
                      onChange={(e) => handleInputChange('letterNamesLowercase', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/26</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">B.</span>
                    <span>Letter names—lowercase</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="21"
                      value={currentStudent.consonantSounds}
                      onChange={(e) => handleInputChange('consonantSounds', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/21</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">C.</span>
                    <span>Consonant sounds</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="5"
                      value={currentStudent.longVowelSounds}
                      onChange={(e) => handleInputChange('longVowelSounds', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/5</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">D.</span>
                    <span>Long vowel sounds</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="5"
                      value={currentStudent.shortVowelSounds}
                      onChange={(e) => handleInputChange('shortVowelSounds', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/5</div>
                  </div>
                  <div className="flex-1 ml-8">
                    <span>Short vowel sounds</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Reading and Decoding Skills Section */}
            <div className="mb-8">
              <h4 className="text-base font-semibold text-thrive-slate mb-4">Reading and Decoding Skills</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="15"
                      value={currentStudent.shortVowelsCVC}
                      onChange={(e) => handleInputChange('shortVowelsCVC', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/15</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">E.</span>
                    <span>Short vowels in CVC words</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="15"
                      value={currentStudent.consonantBlendsShortVowels}
                      onChange={(e) => handleInputChange('consonantBlendsShortVowels', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/15</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">F.</span>
                    <span>Consonant blends with short vowels</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="15"
                      value={currentStudent.shortVowelsDigraphsTch}
                      onChange={(e) => handleInputChange('shortVowelsDigraphsTch', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/15</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">G.</span>
                    <span>Short vowels, digraphs, and <em>-tch</em> trigraph</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="15"
                      value={currentStudent.rControlledVowels}
                      onChange={(e) => handleInputChange('rControlledVowels', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/15</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">H.</span>
                    <span><em>R</em>-controlled vowels</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="15"
                      value={currentStudent.longVowelSpellings}
                      onChange={(e) => handleInputChange('longVowelSpellings', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/15</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">I.</span>
                    <span>Long vowel spellings</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="15"
                      value={currentStudent.variantVowels}
                      onChange={(e) => handleInputChange('variantVowels', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/15</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">J.</span>
                    <span>Variant vowels</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="15"
                      value={currentStudent.lowFrequencySpellings}
                      onChange={(e) => handleInputChange('lowFrequencySpellings', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/15</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">K.</span>
                    <span>Low frequency vowel and consonant spellings</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="24"
                      value={currentStudent.multisyllabicWords}
                      onChange={(e) => handleInputChange('multisyllabicWords', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/24</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">L.</span>
                    <span>Multisyllabic words</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button
                onClick={addStudent}
                className="bg-thrive-mint hover:bg-thrive-teal text-thrive-slate hover:text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-md"
              >
                <SafeIcon icon={FiPlus} />
                Add Student
              </button>
              
              <button
                onClick={processFormData}
                disabled={!isCurrentStudentValid()}
                className="bg-thrive-teal hover:bg-thrive-slate disabled:bg-gray-300 text-white px-8 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-lg disabled:shadow-none"
              >
                Continue to Groups
                <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

export default CorePhonicsSurvey;